import type { RecordRepository, RunRecord } from './types';

export const RECORDS_STORAGE_KEY = 'neon-breaker.runs.v1';
const MAX_RECORDS = 100;

function isRunRecord(value: unknown): value is RunRecord {
  if (!value || typeof value !== 'object') return false;
  const record = value as Partial<RunRecord>;
  return (
    record.schemaVersion === 1 &&
    typeof record.id === 'string' &&
    typeof record.runSeed === 'string' &&
    typeof record.score === 'number' &&
    typeof record.highestLevel === 'number'
  );
}

export class LocalStorageRecordRepository implements RecordRepository {
  constructor(private readonly storage: Storage = globalThis.localStorage) {}

  async saveRun(record: RunRecord): Promise<void> {
    const records = this.readRecords().filter((existing) => existing.id !== record.id);
    records.unshift(record);
    this.storage.setItem(RECORDS_STORAGE_KEY, JSON.stringify(records.slice(0, MAX_RECORDS)));
  }

  async listRuns(limit = 20): Promise<RunRecord[]> {
    return this.readRecords().slice(0, Math.max(0, limit));
  }

  async getBestRecord(): Promise<RunRecord | null> {
    const records = this.readRecords();
    if (records.length === 0) return null;
    return [...records].sort(
      (a, b) => b.score - a.score || b.highestLevel - a.highestLevel || a.durationMs - b.durationMs,
    )[0];
  }

  private readRecords(): RunRecord[] {
    try {
      const rawValue = this.storage.getItem(RECORDS_STORAGE_KEY);
      if (!rawValue) return [];
      const parsed = JSON.parse(rawValue) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isRunRecord).slice(0, MAX_RECORDS);
    } catch {
      return [];
    }
  }
}
